import { createSlice } from "@reduxjs/toolkit";

import { API_URLS, getRequest } from "../../api";

import { getRequestError, sendErrorLogsToDB } from "../../utils/functions";

import { setAlert } from "../components/components-slice";

type Props = {
  isFetchingStoreCampaignMetrics: boolean;
  storeCampaignMetrics: [];
  isFetchingAllCampaignSalesData: boolean;
  campaignSalesData: [];
  isFetchingSalesGeneratingCampaigns: boolean;
  salesGeneratingCampaigns: [];
  isFetchingCustomerMetrics: boolean;
  customerMetrics: [];
  isFetchingHighValueCustomers: boolean;
  highValueCustomers: [];
  isFetchingSalesContribution: boolean;
  salesContribution: [];
  isFetchingKonvasAICampaignStoreTraffic: boolean;
  konvasAICampaignStoreTraffic: [];
};
export const initialState: Props = {
  isFetchingStoreCampaignMetrics: false,
  storeCampaignMetrics: [],
  isFetchingAllCampaignSalesData: false,
  campaignSalesData: [],
  isFetchingSalesGeneratingCampaigns: false,
  salesGeneratingCampaigns: [],
  isFetchingCustomerMetrics: false,
  customerMetrics: [],
  isFetchingHighValueCustomers: false,
  highValueCustomers: [],
  isFetchingSalesContribution: false,
  salesContribution: [],
  isFetchingKonvasAICampaignStoreTraffic: false,
  konvasAICampaignStoreTraffic: [],
};

// Slice
const slice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    setIsFetchingStoreCampaignMetrics: (state, { payload }) => {
      state.isFetchingStoreCampaignMetrics = payload;
    },
    getStoreCampaignMetricsSuccess: (state, { payload }) => {
      state.storeCampaignMetrics = payload;
    },
    setIsFetchingAllCampaignSalesData: (state, { payload }) => {
      state.isFetchingAllCampaignSalesData = payload;
    },
    getAllCampaignSalesDataSuccess: (state, { payload }) => {
      state.campaignSalesData = payload;
    },
    setIsFetchingSalesGeneratingCampaigns: (state, { payload }) => {
      state.isFetchingSalesGeneratingCampaigns = payload;
    },
    getSalesGeneratingCampaignsSuccess: (state, { payload }) => {
      state.salesGeneratingCampaigns = payload;
    },
    setIsFetchingCustomerMetrics: (state, { payload }) => {
      state.isFetchingCustomerMetrics = payload;
    },
    getcustomerMetricsSuccess: (state, { payload }) => {
      state.customerMetrics = payload;
    },
    setIsFetchingHighValueCustomers: (state, { payload }) => {
      state.isFetchingHighValueCustomers = payload;
    },
    getHighValueCustomersSuccess: (state, { payload }) => {
      state.highValueCustomers = payload;
    },

    setIsFetchingSalesContribution: (state, { payload }) => {
      state.isFetchingSalesContribution = payload;
    },
    getSalesContributionSuccess: (state, { payload }) => {
      state.salesContribution = payload;
    },
    setIsFetchingKonvasAICampaignStoreTraffic: (state, { payload }) => {
      state.isFetchingKonvasAICampaignStoreTraffic = payload;
    },
    getKonvasCampaignStoreTrafficSuccess: (state, { payload }) => {
      state.konvasAICampaignStoreTraffic = payload;
    },
  },
});
export default slice.reducer;

// Actions
const {
  setIsFetchingStoreCampaignMetrics,
  getStoreCampaignMetricsSuccess,
  setIsFetchingAllCampaignSalesData,
  getAllCampaignSalesDataSuccess,
  setIsFetchingSalesGeneratingCampaigns,
  getSalesGeneratingCampaignsSuccess,
  setIsFetchingCustomerMetrics,
  getcustomerMetricsSuccess,
  setIsFetchingHighValueCustomers,
  getHighValueCustomersSuccess,
  setIsFetchingSalesContribution,
  getSalesContributionSuccess,
  setIsFetchingKonvasAICampaignStoreTraffic,
  getKonvasCampaignStoreTrafficSuccess,
} = slice.actions;

//Campaign Analytics

export const getStoreCampaignMetricsAction =
  (params: { range: string } | { start: string; end: string }) =>
  async (dispatch: any) => {
    dispatch(setIsFetchingStoreCampaignMetrics(true));

    try {
      const res = await getRequest({
        url: `${API_URLS?.getStoreCampaignMetrics}`,
        params,
      });

      dispatch(setIsFetchingStoreCampaignMetrics(false));
      dispatch(getStoreCampaignMetricsSuccess(res?.data?.data));

      return {
        success: true,
      };
    } catch (err) {
      const errorMessage = getRequestError(err);
      dispatch(setAlert(true, "error", errorMessage));

      dispatch(setIsFetchingStoreCampaignMetrics(false));
      await sendErrorLogsToDB(
        errorMessage,
        "analytics",
        "get store campaign metrics"
      );
    }
  };

export const getAllCampaignSalesDataAction = () => async (dispatch: any) => {
  dispatch(setIsFetchingAllCampaignSalesData(true));

  try {
    const res = await getRequest({
      url: `${API_URLS?.getAllCampaignSalesData}`,
      params: null,
    });

    dispatch(setIsFetchingAllCampaignSalesData(false));
    dispatch(getAllCampaignSalesDataSuccess(res?.data?.campaigns));

    return {
      success: true,
    };
  } catch (err) {
    const errorMessage = getRequestError(err);
    dispatch(setAlert(true, "error", errorMessage));

    dispatch(setIsFetchingAllCampaignSalesData(false));
    await sendErrorLogsToDB(
      errorMessage,
      "analytics",
      "get all campaign sales data"
    );
  }
};

export const getSalesGeneratingCampaignsAction =
  () => async (dispatch: any) => {
    dispatch(setIsFetchingSalesGeneratingCampaigns(true));

    try {
      const res = await getRequest({
        url: `${API_URLS?.getSalesGeneratingCampaigns}`,
        params: null,
      });

      dispatch(setIsFetchingSalesGeneratingCampaigns(false));
      dispatch(getSalesGeneratingCampaignsSuccess(res?.data?.campaigns));

      return {
        success: true,
      };
    } catch (err) {
      const errorMessage = getRequestError(err);
      dispatch(setAlert(true, "error", errorMessage));

      dispatch(setIsFetchingSalesGeneratingCampaigns(false));
      await sendErrorLogsToDB(
        errorMessage,
        "analytics",
        "get sales generating campaigns"
      );
    }
  };

export const getSalesContributionAction =
  (params: { range: string }) => async (dispatch: any) => {
    dispatch(setIsFetchingSalesContribution(true));

    try {
      const res = await getRequest({
        url: `${API_URLS?.getSalesContribution}`,
        params,
      });

      dispatch(setIsFetchingSalesContribution(false));
      dispatch(getSalesContributionSuccess(res?.data?.data));

      return {
        success: true,
      };
    } catch (err) {
      const errorMessage = getRequestError(err);
      dispatch(setAlert(true, "error", errorMessage));

      dispatch(setIsFetchingSalesContribution(false));
      await sendErrorLogsToDB(
        errorMessage,
        "analytics",
        "get konvas sales contribution data"
      );
    }
  };

export const getKonvasAICampaignStoreTrafficAction =
  (params: { range: string }) => async (dispatch: any) => {
    dispatch(setIsFetchingKonvasAICampaignStoreTraffic(true));

    try {
      const res = await getRequest({
        url: `${API_URLS?.getKonvasAICampaignStoreTraffic}`,
        params,
      });

      dispatch(setIsFetchingKonvasAICampaignStoreTraffic(false));
      dispatch(getKonvasCampaignStoreTrafficSuccess(res?.data?.data));

      return {
        success: true,
      };
    } catch (err) {
      const errorMessage = getRequestError(err);
      dispatch(setAlert(true, "error", errorMessage));

      dispatch(setIsFetchingKonvasAICampaignStoreTraffic(false));
      await sendErrorLogsToDB(
        errorMessage,
        "analytics",
        "get store traffic from Konvas AI campaigns"
      );
    }
  };

//Customer Analytics
export const getCustomerMetricsAction =
  (params: { range: string }) => async (dispatch: any) => {
    dispatch(setIsFetchingCustomerMetrics(true));

    try {
      const res = await getRequest({
        url: `${API_URLS?.getCustomerMetrics}`,
        params,
      });

      dispatch(setIsFetchingCustomerMetrics(false));
      dispatch(getcustomerMetricsSuccess(res?.data?.data));

      return {
        success: true,
      };
    } catch (err) {
      const errorMessage = getRequestError(err);
      dispatch(setAlert(true, "error", errorMessage));

      dispatch(setIsFetchingCustomerMetrics(false));
      await sendErrorLogsToDB(
        errorMessage,
        "analytics",
        "get customer metrics"
      );
    }
  };

export const getHighValueCustomersAction = () => async (dispatch: any) => {
  dispatch(setIsFetchingHighValueCustomers(true));

  try {
    const res = await getRequest({
      url: `${API_URLS?.getHighValueCustomers}`,
      params: null,
    });

    dispatch(setIsFetchingHighValueCustomers(false));
    dispatch(getHighValueCustomersSuccess(res?.data?.data));

    return {
      success: true,
    };
  } catch (err) {
    const errorMessage = getRequestError(err);
    dispatch(setAlert(true, "error", errorMessage));

    dispatch(setIsFetchingHighValueCustomers(false));
    await sendErrorLogsToDB(
      errorMessage,
      "analytics",
      "get high value customers"
    );
  }
};
